import React, { useState } from "react";
import { X, Microphone, Sparkle, MusicNotes, Globe, Users, Lock, Check } from "@phosphor-icons/react";
import { createPortal } from "react-dom";
import { api, uploadFile, fileUrl } from "@/lib/api";
import { fileFromAudioBlob } from "@/lib/audioMime";
import { PHOTO_FILTERS } from "@/lib/photoFilters";
import VoiceRecorder from "@/components/VoiceRecorder";
import MusicPicker from "@/components/MusicPicker";
import WaveformPlayer from "@/components/WaveformPlayer";
import { toast } from "sonner";

const AUDIENCES = [
  { id: "public", label: "Public", icon: Globe, desc: "Anyone on Voxly" },
  { id: "followers", label: "Followers", icon: Users, desc: "Only people who follow you" },
  { id: "close", label: "Close friends", icon: Lock, desc: "Just your inner circle" },
];

/**
 * EditPostDialog — voice-first post editor.
 *
 * Lets the owner re-record voice, swap photo filter, change background music,
 * or update audience after posting. Media (photo/video) is intentionally NOT
 * editable — delete + repost for that case.
 */
export default function EditPostDialog({ open, onClose, post, onSaved }) {
  const [filterId, setFilterId] = useState(post?.photo_filter || "none");
  const [audience, setAudience] = useState(post?.audience || "public");
  const [music, setMusic] = useState(post?.music_url
    ? { url: post.music_url, title: post.music_title || "Music" }
    : null);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const [voicePreview, setVoicePreview] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showMusicPicker, setShowMusicPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasMedia = !!post?.media_path;
  const isVideo = post?.media_type === "video";

  const save = async () => {
    setSaving(true);
    try {
      const updates = {
        photo_filter: filterId === "none" ? null : filterId,
        audience,
        music_url: music?.url || null,
        music_title: music?.title || null,
      };
      if (voiceBlob) {
        const file = fileFromAudioBlob(voiceBlob, "post-voice");
        const up = await uploadFile(file, "audio");
        updates.audio_path = up.path;
        updates.duration = voiceDuration;
      }
      await api.patch(`/posts/${post.post_id}`, updates);
      toast.success("Post updated");
      onSaved?.({ ...post, ...updates, edited_at: new Date().toISOString() });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save changes");
    } finally { setSaving(false); }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center" data-testid="edit-post-dialog">
      <div className="w-full max-w-md sm:rounded-3xl rounded-t-3xl bg-card border border-white/10 shadow-2xl max-h-[92vh] flex flex-col">
        <header className="px-5 py-4 border-b border-border flex items-center justify-between shrink-0">
          <h3 className="font-heading text-lg font-bold">Edit post</h3>
          <button onClick={onClose} aria-label="Close" data-testid="edit-close" className="h-9 w-9 rounded-full bg-secondary/60 flex items-center justify-center text-muted-foreground active:scale-90 transition">
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto p-5 space-y-5">
          {/* Voice — re-record */}
          <section data-testid="edit-voice-section">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Voice</h4>
              {voiceBlob && (
                <button onClick={() => { setVoiceBlob(null); setVoicePreview(null); setVoiceDuration(0); }} className="text-[10px] font-bold text-destructive uppercase tracking-wider">
                  Discard new
                </button>
              )}
            </div>
            {voicePreview ? (
              <div className="rounded-2xl border border-primary/40 bg-primary/5 px-3 py-2">
                <div className="flex items-center gap-2 mb-1 text-[10px] font-bold text-primary uppercase tracking-widest">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> New recording
                </div>
                <audio src={voicePreview} controls className="w-full" />
              </div>
            ) : post.audio_path ? (
              <div className="rounded-2xl bg-secondary/40 p-2">
                <WaveformPlayer src={fileUrl(post.audio_path)} duration={post.duration} compact testid="current-voice" />
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No voice on this post.</p>
            )}
            <button
              type="button"
              onClick={() => setShowRecorder(true)}
              data-testid="edit-rerecord"
              className="mt-2 w-full h-11 rounded-full bg-primary/15 border border-primary/30 text-primary text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition"
            >
              <Microphone size={16} weight="fill" /> {post.audio_path || voiceBlob ? "Re-record voice" : "Add voice"}
            </button>
          </section>

          {/* Filter strip — only for photos */}
          {hasMedia && !isVideo && (
            <section data-testid="edit-filter-section">
              <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> Filter
              </h4>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar">
                {PHOTO_FILTERS.map((f) => {
                  const id = f.id || "none";
                  const active = filterId === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setFilterId(id)}
                      data-testid={`edit-filter-${id}`}
                      className={`shrink-0 flex flex-col items-center gap-1 active:scale-95 transition ${active ? "" : "opacity-90"}`}
                    >
                      <div className={`h-14 w-14 rounded-xl overflow-hidden border-2 transition ${active ? "border-primary ring-2 ring-primary/40 scale-105" : "border-white/15"}`}>
                        <img src={fileUrl(post.media_path)} alt="" className="w-full h-full object-cover" style={{ filter: f.css }} loading="lazy" />
                      </div>
                      <span className={`text-[10px] font-bold ${active ? "text-primary" : "text-foreground/80"}`}>{f.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Music */}
          <section data-testid="edit-music-section">
            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
              <MusicNotes size={12} weight="fill" /> Background music
            </h4>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowMusicPicker(true)}
                data-testid="edit-music-pick"
                className="flex-1 h-11 px-3 rounded-full bg-secondary/60 border border-border text-sm font-semibold flex items-center justify-between active:scale-[0.98] transition"
              >
                <span className="truncate text-left">{music?.title || "Pick a track"}</span>
                <MusicNotes size={14} weight="fill" className="text-primary shrink-0" />
              </button>
              {music && (
                <button
                  type="button"
                  onClick={() => setMusic(null)}
                  data-testid="edit-music-clear"
                  className="text-[10px] font-bold text-destructive uppercase tracking-wider"
                >
                  Clear
                </button>
              )}
            </div>
          </section>

          {/* Audience */}
          <section data-testid="edit-audience-section">
            <h4 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Who can see this</h4>
            <div className="space-y-1.5">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const active = audience === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAudience(a.id)}
                    data-testid={`edit-audience-${a.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition ${active ? "border-primary bg-primary/10" : "border-border bg-secondary/30 hover:bg-secondary/60"}`}
                  >
                    <Icon size={20} weight="fill" className={active ? "text-primary" : "text-muted-foreground"} />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-bold">{a.label}</p>
                      <p className="text-[11px] text-muted-foreground">{a.desc}</p>
                    </div>
                    {active && <Check size={18} weight="bold" className="text-primary" />}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex gap-3 shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-3 rounded-full bg-secondary text-foreground text-sm font-bold active:scale-95 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="edit-save"
            className="flex-1 py-3 rounded-full bg-primary text-primary-foreground text-sm font-bold uppercase tracking-wider active:scale-95 transition glow-hover disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <VoiceRecorder
        open={showRecorder}
        onClose={() => setShowRecorder(false)}
        onComplete={({ blob, duration }) => {
          setVoiceBlob(blob);
          setVoiceDuration(duration);
          setVoicePreview(URL.createObjectURL(blob));
          setShowRecorder(false);
        }}
        title="Re-record voice"
        subtitle="Replaces the voice on your post"
      />
      <MusicPicker
        open={showMusicPicker}
        onClose={() => setShowMusicPicker(false)}
        onPick={(t) => { setMusic(t); setShowMusicPicker(false); }}
        selectedId={music?.id}
      />
    </div>,
    document.body,
  );
}
